import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY as empty } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { JssPlatformService } from '@core/jss-platform';
import { toSignal } from '@angular/core/rxjs-interop';
import { ComponentRendering, JssModule } from '@core/jss-proxy';

import {
  JssStateFatalError,
  JssStatePlaceholderLoaded,
} from '../+state/jss-route.actions';
import {
  getContext,
  getRenderingData,
  getSitecore,
} from '../+state/jss.selectors';
import { layouts, OBSOLETE_ROOT_PLACEHOLDER } from './layouts.constants';

@Component({
  selector: 'wl-jss-route',
  templateUrl: './jss-route.component.html',
  standalone: true,
  imports: [JssModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JssRouteComponent {
  readonly jssPlatform = inject(JssPlatformService);
  readonly #store$ = inject(Store);

  readonly rendering = toSignal(
    this.#store$.pipe(
      getRenderingData,
      catchError((error: unknown) => {
        this.#store$.dispatch(
          new JssStateFatalError({
            route: 'ServerError',
            error: error as Error,
          })
        );
        return empty;
      })
    )
  );

  readonly sitecoreData = this.#store$.selectSignal(getSitecore);
  readonly context = this.#store$.selectSignal(getContext);

  readonly componentRendering = computed(() => {
    const sitecoreRouteData = this.rendering();
    if (sitecoreRouteData) {
      return sitecoreRouteData as unknown as ComponentRendering;
    }

    return undefined;
  });

  readonly layoutId = computed(
    () => this.rendering()?.layoutId as keyof typeof layouts | undefined
  );

  readonly placeholders = computed(() => {
    const layoutId = this.layoutId();

    return layoutId && layoutId in layouts ? layouts[layoutId] : [];
  });

  fallbackPlaceholder = OBSOLETE_ROOT_PLACEHOLDER;

  triggerLoaded(placeholderName?: string) {
    this.#store$.dispatch(new JssStatePlaceholderLoaded(placeholderName));
  }

  constructor() {
    effect(() => {
      const sitecoreDataLoaded = !!this.sitecoreData();

      if (sitecoreDataLoaded) {
        const layoutId = this.layoutId();
        const path = this.context()?.['itemPath'];

        if (!layoutId) {
          console.error(
            `No layout detected on "${path}". This page is now rendering "${this.fallbackPlaceholder}" as fallback`
          );
        }

        if (layoutId && !(layoutId in layouts)) {
          console.error(
            `Unsupported layout detected on "${path}" (layoutId: ${layoutId}). This page is now rendering "${this.fallbackPlaceholder}" as fallback`
          );
        }
      }
    });
  }
}
